import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  Paper,
  Typography,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import api from "../../api/api";
import { useErrorHandler } from "../../../hooks/useErrorHandler";

const StaffEditPage = () => {
  const { id } = useParams(); // Получаем ID из URL
  const navigate = useNavigate();
  const { handleError, showSuccess } = useErrorHandler();
  const [staff, setStaff] = useState(null);
  const [saving, setSaving] = useState(false);
  const savePending = useRef(false);
  const [loading, setLoading] = useState(true);
  const [rentalPoints, setRentalPoints] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const fetchRentalPoints = async () => {
      try {
        const response = await api.get("/staff/rental-points", {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        if (!cancelled) setRentalPoints(response.data || []);
      } catch (error) {
        if (cancelled) return;
        console.error("Ошибка загрузки пунктов проката:", error);
        handleError(error, "Не удалось загрузить список пунктов проката.");
      }
    };

    fetchRentalPoints();
    if (id === "new") {
      // Если новый сотрудник, отображаем пустую форму
      setStaff({
        last_name: "",
        first_name: "",
        middle_name: "",
        email: "",
        phone: "",
        address: "",
        date_of_birth: "",
        passport_issue_date: "",
        passport_series: "",
        passport_number: "",
        passport_issued_by: "",
        passport_division_code: "",
        rental_points: "",
      });
      setLoading(false);
      return () => { cancelled = true; };
    }

    const fetchStaff = async () => {
      setLoading(true);
      setStaff(null);
      try {
        const response = await api.get(`/staff/${id}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        
        // Преобразуем даты в формат для input type="date" (YYYY-MM-DD)
        if (cancelled) return;
        const staffData = {...response.data};
        if (staffData.date_of_birth) {
          // Если дата в ISO формате, извлекаем только дату
          staffData.date_of_birth = staffData.date_of_birth.split('T')[0];
        }
        if (staffData.passport_issue_date) {
          // Если дата в ISO формате, извлекаем только дату
          staffData.passport_issue_date = staffData.passport_issue_date.split('T')[0];
        }
        
        setStaff(staffData);
      } catch (error) {
        if (cancelled) return;
        console.error("Ошибка загрузки данных сотрудника:", error);
        if (error.response && error.response.status === 404) {
          handleError(error, "Сотрудник не найден.");
        } else {
          handleError(error, "Не удалось загрузить данные сотрудника.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchStaff();
    return () => { cancelled = true; };
  }, [id]);

  const handleSave = async () => {
    if (savePending.current) return;
    if (!staff.password && id === "new") {
      handleError(new Error("Пароль обязателен для нового сотрудника."));
      return;
    }

    savePending.current = true;
    setSaving(true);
    try {
      const endpoint = id === "new" ? "/staff" : `/staff/${id}`;
      const method = id === "new" ? "post" : "put";
      await api[method](endpoint, staff, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      showSuccess("Сотрудник успешно сохранён!");
      navigate("/staff");
    } catch (error) {
      console.error("Ошибка сохранения данных сотрудника:", error);
      
      // Определяем тип ошибки и показываем соответствующее сообщение
      if (error.response && error.response.status === 400) {
        const detail = error.response.data?.detail;
        const errorDetail = typeof detail === 'string' ? detail : '';
        
        if (errorDetail.includes('email')) {
          handleError(error, "Сотрудник с таким email уже существует. Пожалуйста, используйте другой email.");
        } else if (errorDetail.includes('номер') || errorDetail.includes('number')) {
          handleError(error, "Сотрудник с таким номером уже существует.");
        } else if (errorDetail.includes('телефон') || errorDetail.includes('phone')) {
          handleError(error, "Сотрудник с таким номером телефона уже существует.");
        } else {
          handleError(error, "Ошибка при сохранении сотрудника: " + errorDetail);
        }
      } else if (error.response && error.response.status === 422) {
        handleError(error, "Проверьте правильность заполнения всех полей.");
      } else {
        handleError(error, "Не удалось сохранить сотрудника. Проверьте соединение с сервером.");
      }
    } finally {
      savePending.current = false;
      setSaving(false);
    }
  };

  if (loading) {
    return <Typography>Загрузка...</Typography>;
  }

  if (!staff) {
    return <Typography>Сотрудник не найден.</Typography>;
  }

  return (
    <Box
      sx={{
        padding: "20px",
        maxWidth: "100%",
        margin: "0 auto",
      }}
    >
      {/* Кнопка Назад */}
      <Box sx={{ marginBottom: "20px" }}>
        <Button variant="outlined" onClick={() => navigate("/staff")}>
          Назад
        </Button>
      </Box>

      {/* Блок Основное */}
      <Paper elevation={3} sx={{ padding: "20px", marginBottom: "20px" }}>
        <Typography variant="h6">Основное</Typography>
        <Divider sx={{ marginY: "10px" }} />
        <Box
          sx={{
            display: "grid",
            gap: "20px",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          }}
        >
          <TextField
            label="Фамилия"
            value={staff.last_name}
            onChange={(e) => setStaff({ ...staff, last_name: e.target.value })}
            fullWidth
            variant="outlined"
          />
          <TextField
            label="Имя"
            value={staff.first_name}
            onChange={(e) => setStaff({ ...staff, first_name: e.target.value })}
            fullWidth
            variant="outlined"
          />
          <TextField
            label="Отчество"
            value={staff.middle_name}
            onChange={(e) =>
              setStaff({ ...staff, middle_name: e.target.value })
            }
            fullWidth
            variant="outlined"
          />
        </Box>
      </Paper>

      {/* Блок Учётные данные */}
      <Paper elevation={3} sx={{ padding: "20px", marginBottom: "20px" }}>
        <Typography variant="h6">Учётные данные</Typography>
        <Divider sx={{ marginY: "10px" }} />
        <Box
          sx={{
            display: "grid",
            gap: "20px",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          }}
        >
          <TextField
            label="Логин"
            value={staff.email}
            onChange={(e) => setStaff({ ...staff, email: e.target.value })}
            fullWidth
            variant="outlined"
          />
          <TextField
            label="Новый пароль"
            type="password"
            fullWidth
            variant="outlined"
            value={staff.password || ""} // Устанавливаем значение
            onChange={(e) =>
              setStaff((prev) => ({
                ...prev,
                password: e.target.value,
              }))
            }
          />

          <TextField
            label="Дата рождения"
            type="date"
            value={staff.date_of_birth || ""}
            onChange={(e) =>
              setStaff({ ...staff, date_of_birth: e.target.value })
            }
            fullWidth
            variant="outlined"
            InputLabelProps={{ shrink: true }}
          />
        </Box>
      </Paper>

      {/* Блок Паспортные данные */}
      <Paper elevation={3} sx={{ padding: "20px", marginBottom: "20px" }}>
        <Typography variant="h6">Паспортные данные</Typography>
        <Divider sx={{ marginY: "10px" }} />
        <Box
          sx={{
            display: "grid",
            gap: "20px",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          }}
        >
          <TextField
            label="Серия"
            value={staff.passport_series || ""}
            onChange={(e) =>
              setStaff({ ...staff, passport_series: e.target.value })
            }
            fullWidth
            variant="outlined"
          />
          <TextField
            label="Номер"
            value={staff.passport_number || ""}
            onChange={(e) =>
              setStaff({ ...staff, passport_number: e.target.value })
            }
            fullWidth
            variant="outlined"
          />
          <TextField
            label="Где выдан"
            value={staff.passport_issued_by || ""}
            onChange={(e) =>
              setStaff({ ...staff, passport_issued_by: e.target.value })
            }
            fullWidth
            variant="outlined"
          />
          <TextField
            label="Код подразделения"
            value={staff.passport_division_code || ""}
            onChange={(e) =>
              setStaff({ ...staff, passport_division_code: e.target.value })
            }
            fullWidth
            variant="outlined"
          />
          <TextField
            label="Дата выдачи"
            type="date"
            value={staff.passport_issue_date || ""}
            onChange={(e) =>
              setStaff({ ...staff, passport_issue_date: e.target.value })
            }
            fullWidth
            variant="outlined"
            InputLabelProps={{ shrink: true }}
          />
        </Box>
      </Paper>

      {/* Блок Контакты */}
      <Paper elevation={3} sx={{ padding: "20px", marginBottom: "20px" }}>
        <Typography variant="h6">Контакты</Typography>
        <Divider sx={{ marginY: "10px" }} />
        <TextField
          label="Телефон"
          value={staff.phone}
          onChange={(e) => setStaff({ ...staff, phone: e.target.value })}
          fullWidth
          variant="outlined"
        />
        <TextField
          label="Адрес"
          value={staff.address || ""}
          onChange={(e) => setStaff({ ...staff, address: e.target.value })}
          fullWidth
          variant="outlined"
        />
      </Paper>

      {/* Блок Доступ к работе пункты проката */}
      <Paper elevation={3} sx={{ padding: "20px", marginBottom: "20px" }}>
        <Typography variant="h6">Доступ к работе пункты проката</Typography>
        <Divider sx={{ marginY: "10px" }} />
        <FormControl fullWidth variant="outlined">
          <InputLabel>Пункт проката</InputLabel>
          <Select
            value={staff.rental_point_id || ""}
            onChange={(e) =>
              setStaff({ ...staff, rental_point_id: e.target.value })
            }
            label="Пункт проката"
          >
            <MenuItem value="">
              <em>Не назначен</em>
            </MenuItem>
            {rentalPoints.map((point) => (
              <MenuItem key={point.id} value={point.id}>
                {point.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      {/* Блок Изображения */}
      <Paper elevation={3} sx={{ padding: "20px" }}>
        <Typography variant="h6">Изображения</Typography>
        <Divider sx={{ marginY: "10px" }} />
        <Box
          sx={{
            border: "2px dashed #ccc",
            padding: "20px",
            textAlign: "center",
          }}
        >
          Выберите файлы или перетащите их в эту область
        </Box>
      </Paper>

      {/* Кнопка Сохранить */}
      <Box sx={{ marginTop: "20px", textAlign: "right" }}>
        <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}>
          Сохранить
        </Button>
      </Box>
    </Box>
  );
};

export default StaffEditPage;
